import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useApp } from "../context/AppContext";
import { api } from "../lib/api";

type Accepted = {
  accepted: boolean;
  organisation: string;
  organisation_code: string;
  role: string;
};

export function JoinPage() {
  const { authenticated, loading, switchOrganisation } = useApp();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState<Accepted | null>(null);

  const valid = /^[A-Za-z0-9_-]{32,128}$/.test(token);
  const next = "/join?token=" + encodeURIComponent(token);
  const startUrl = "/api/v1/auth/oidc/start/?next=" + encodeURIComponent(next);

  async function accept() {
    if (!valid || !authenticated) return;
    setBusy(true);
    setError("");
    try {
      const result = await api.post<Accepted>("/api/v1/auth/oidc/invitations/accept/", { token }, null);
      await switchOrganisation(result.organisation_code);
      setAccepted(result);
      window.history.replaceState({}, "", "/join");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Invitation impossible à accepter.");
    } finally {
      setBusy(false);
    }
  }

  return <main className="min-h-screen bg-slate-50 p-5">
    <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-7 shadow-sm">
      <img className="h-12 w-auto" src="/saheltech-mark.svg" alt="SahelTech" />
      <h1 className="mt-7 text-3xl font-black text-slate-950">Rejoindre une entreprise</h1>
      <p className="mt-3 text-sm text-slate-600">Une invitation permet d'accéder à une entreprise avec le rôle fixé par son responsable. Elle ne donne aucun droit avant votre validation.</p>

      {!valid && !accepted && <p role="alert" className="mt-6 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">Lien d'invitation invalide.</p>}
      {valid && loading && <p className="mt-6 text-sm">Vérification de votre compte…</p>}
      {valid && !loading && !authenticated && <div className="mt-6 space-y-3">
        <a href={startUrl} className="btn-primary flex justify-center">Continuer avec SahelTech Compte</a>
        <p className="text-sm text-slate-500">Vous pourrez utiliser Google sur le portail Accounts. Aucun mot de passe entreprise n'est nécessaire.</p>
      </div>}
      {valid && authenticated && !accepted && <div className="mt-6">
        <p className="mb-4 text-sm font-semibold text-slate-800">Vous êtes connecté. Confirmez votre accord pour rejoindre cette entreprise.</p>
        <button className="btn-primary w-full" disabled={busy} onClick={() => void accept()}>
          {busy ? "Vérification…" : "Accepter l'invitation"}
        </button>
      </div>}
      {error && <p role="alert" className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">{error}</p>}
      {accepted && <div className="mt-6 space-y-4">
        <p className="rounded-xl bg-emerald-50 p-4 font-semibold text-emerald-900">Vous avez rejoint {accepted.organisation} en tant que {accepted.role}.</p>
        <Link to="/" className="btn-primary flex justify-center">Accéder à mon espace</Link>
      </div>}
    </div>
  </main>;
}
